import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  api,
  type OpsResponse,
  type SyncStatus,
  type UsageSummary,
} from "../../lib/api";
import { formatCost, formatNumber, timeAgo } from "../../lib/format";
import {
  Banner,
  Card,
  Empty,
  ErrorNote,
  Metric,
  PageHeader,
  Spinner,
  Tabs,
  useToast,
} from "../../components/ui";
import { Evaluation } from "./EvaluationTab";
import { Settings } from "./SettingsTab";

export function AdminScreen() {
  const [tab, setTab] = useState("cost");

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Admin panel"
        subtitle="Metering, connector operations, observability and platform health"
      />
      <Tabs
        tabs={[
          { id: "cost", label: "Cost & usage" },
          { id: "sync", label: "Connector" },
          { id: "eval", label: "Observability" },
          { id: "ops", label: "Operations" },
          { id: "settings", label: "Settings" },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === "cost" && <Usage />}
      {tab === "sync" && <Sync />}
      {tab === "eval" && <Evaluation />}
      {tab === "ops" && <Operations />}
      {tab === "settings" && <Settings />}
    </>
  );
}

function Usage() {
  const [range, setRange] = useState("7d");
  const { data, isLoading, error } = useQuery({
    queryKey: ["usage", range],
    queryFn: () => api.get<UsageSummary>("/admin/usage", { range }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {["24h", "7d", "30d", "90d"].map((option) => (
          <button
            key={option}
            onClick={() => setRange(option)}
            className={`rounded-full px-2.5 py-1 text-sm font-bold ${
              range === option
                ? "bg-ink text-pearl"
                : "border border-line bg-surface text-muted-strong hover:bg-surface-hover"
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Metric label="Questions" value={formatNumber(data.queries)} />
        <Metric label="Tokens in" value={formatNumber(data.tokensIn)} />
        <Metric label="Tokens out" value={formatNumber(data.tokensOut)} />
        <Metric label="Average per query" value={formatNumber(data.averageTokens)} />
        <Metric
          label="Estimated cost"
          value={formatCost(data.estimatedCost)}
          trend={`${data.tokenCoveragePct}% of queries measured`}
          tone={data.tokenCoveragePct > 90 ? "good" : "neutral"}
        />
      </div>

      <Card className="mb-4" title={`Token volume · ${data.range}`}>
        {data.queries === 0 ? (
          <Empty
            title="No questions asked in this period"
            detail="Cost and token figures are derived from real usage events, so they stay at zero until the assistant is used."
          />
        ) : (
          <>
            <div className="flex h-40 items-end gap-1.5">
              {data.bars.map((height, index) => (
                // The column must carry the row's full height: a percentage
                // height on the bar resolves against its parent, and a parent
                // that only wraps its content is zero pixels tall.
                <div
                  key={index}
                  className="flex h-full flex-1 flex-col justify-end"
                  title={`${data.barLabels[index]}`}
                >
                  <div
                    className={`w-full rounded-t-[3px] transition-[height] duration-500 ${
                      height > 0 ? "bg-accent" : "bg-surface-rail"
                    }`}
                    // A day with no usage still gets a hairline, so the axis
                    // reads as a series of days rather than a gap.
                    style={{ height: height > 0 ? `${Math.max(height, 2)}%` : "2px" }}
                  />
                </div>
              ))}
            </div>
            <div className="mt-1.5 flex gap-1.5">
              {data.barLabels.map((label, index) => {
                // Thirty daily bars leave about twenty pixels per label, which
                // is not enough for "09 Aug". Thin them to roughly a dozen and
                // let the bar tooltips carry the rest.
                const every = Math.ceil(data.barLabels.length / 12);
                const shown = index % every === 0;
                return (
                  <span
                    key={index}
                    className="flex-1 truncate text-center text-[8px] text-muted-soft"
                  >
                    {shown ? label : " "}
                  </span>
                );
              })}
            </div>
          </>
        )}
      </Card>

      <Card title="By model" collapsible storageKey="by-model" defaultOpen={false} padded={false}>
        {data.byModel.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted">No usage yet.</p>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="table-head">
                <th className="px-4 py-2.5">Model</th>
                <th className="px-3 py-2.5">Questions</th>
                <th className="px-3 py-2.5">Tokens</th>
                <th className="px-3 py-2.5">Estimated cost</th>
              </tr>
            </thead>
            <tbody>
              {data.byModel.map((row) => (
                <tr key={row.model} className="border-b border-line-soft last:border-0">
                  <td className="px-4 py-2.5 text-sm font-bold text-ink">
                    {row.model}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-muted-strong">
                    {formatNumber(row.queries)}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-muted-strong">
                    {formatNumber(row.tokens)}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-muted-strong">
                    {formatCost(row.cost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="border-t border-line-soft px-4 py-2.5 text-2xs text-muted-soft">
          Cost is estimated from configured per-model pricing. Where a provider
          reports no usage, the query is counted but its tokens are not — which
          is what the coverage figure measures.
        </p>
      </Card>
    </>
  );
}

function Sync() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["sync"],
    queryFn: () => api.get<SyncStatus>("/admin/sync"),
    refetchInterval: 15_000,
  });

  const run = useMutation({
    mutationFn: (reset: boolean) =>
      api.post<{ message: string }>("/admin/sync/run", undefined, {
        reset_token: reset,
      }),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["sync"] });
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  return (
    <>
      {!data.isOnline && (
        <Banner tone="warn">
          The connector is offline. Approvals are being queued for publish and
          will complete automatically when the connection returns. The assistant
          keeps answering from the local index.
        </Banner>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          className="btn-primary"
          disabled={run.isPending}
          onClick={() => run.mutate(false)}
        >
          Run delta sync
        </button>
        <button
          className="btn-ghost"
          disabled={run.isPending}
          onClick={() => {
            if (
              window.confirm(
                "Resetting the token forces a full sweep of the whole library. Continue?",
              )
            )
              run.mutate(true);
          }}
        >
          Reset token
        </button>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <Metric label="Source" value={data.siteLabel || data.provider} />
        <Metric label="Last run" value={timeAgo(data.lastRunAt)} trend={data.lastRunStatus} />
        <Metric label="Next run" value={timeAgo(data.nextRunAt)} />
        <Metric label="Items pending" value={String(data.itemsPending)} />
        <Metric
          label="Failures (24h)"
          value={String(data.failures24h)}
          tone={data.failures24h ? "bad" : "good"}
        />
        <Metric label="Libraries watched" value={String(data.librariesWatched)} />
      </div>

      <Card className="mb-4" title="Connector" collapsible storageKey="connector" defaultOpen={false}>
        <dl className="space-y-2 text-sm">
          <Row label="Authentication" value={data.authMode} />
          <Row
            label="Change notifications"
            value={
              data.supportsWebhooks
                ? data.webhookSubscriptionId
                  ? `Active · expires ${timeAgo(data.webhookExpiresAt)}`
                  : "Supported, not subscribed"
                : "Not supported by this provider"
            }
          />
          <Row
            label="Check-out"
            value={
              data.supportsCheckout
                ? "Acquired at source"
                : "Advisory within the DMS only"
            }
          />
          <Row label="Delta token" value={data.deltaToken ? "Stored" : "None — next run is a full sweep"} />
        </dl>
        {data.deltaToken && (
          <pre className="mt-3 overflow-x-auto rounded-field bg-surface-sunken p-2.5 text-2xs text-muted">
            {data.deltaToken.slice(0, 240)}
            {data.deltaToken.length > 240 && "…"}
          </pre>
        )}
        {data.lastError && (
          <p
            className="mt-3 rounded-card px-3 py-2 text-2xs"
            style={{ background: "#FFECEC", color: "#B23438" }}
          >
            {data.lastError}
          </p>
        )}
      </Card>

      <Card title="How storage works" collapsible storageKey="how-storage-works" defaultOpen={false}>
        <p className="text-sm leading-relaxed text-muted-strong">
          The document library remains the system of record. This system stores
          only extracted text, passages and embeddings, keyed to the source item
          id and its eTag — there is no second copy of a controlled document to
          reconcile or defend at audit. Changes arrive by change notification and
          are reconciled by a scheduled sweep. Permissions are evaluated at query
          time rather than cached in the index, so a permission change at source
          takes effect on the next question rather than the next sync.
        </p>
      </Card>
    </>
  );
}

function Operations() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["operations"],
    queryFn: () => api.get<OpsResponse>("/admin/operations"),
    refetchInterval: 30_000,
  });

  const { data: sync } = useQuery({
    queryKey: ["sync"],
    queryFn: () => api.get<SyncStatus>("/admin/sync"),
  });

  const offline = useMutation({
    mutationFn: (goOffline: boolean) =>
      api.post<{ message: string }>("/admin/operations/offline", undefined, {
        offline: goOffline,
      }),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["sync"] });
      void queryClient.invalidateQueries({ queryKey: ["operations"] });
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {data.stats.map((stat) => (
          <Metric
            key={stat.key}
            label={stat.key}
            value={stat.value}
            tone={stat.tone}
          />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Integrations" padded={false}>
          <ul>
            {data.integrations.map((integration) => (
              <li
                key={integration.name}
                className="flex items-center gap-3 border-b border-line-soft px-4 py-3 last:border-0"
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{
                    background:
                      integration.state === "Connected" ? "#8DE971" : "#DDD7DA",
                  }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-base font-bold text-ink">
                    {integration.name}
                  </span>
                  <span className="block truncate text-2xs text-muted-soft">
                    {integration.detail}
                  </span>
                </span>
                <span className="text-2xs font-bold text-muted">
                  {integration.state}
                </span>
              </li>
            ))}
          </ul>
        </Card>

        <div className="space-y-4">
          <Card title="Provider health" collapsible storageKey="provider-health" defaultOpen={false}>
            <ul className="space-y-2">
              {Object.entries(data.providerHealth).map(([name, healthy]) => (
                <li key={name} className="flex items-center gap-2.5 text-sm">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: healthy ? "#8DE971" : "#B23438" }}
                  />
                  <span className="flex-1 capitalize text-muted-strong">{name}</span>
                  <span className="text-2xs font-bold text-muted-soft">
                    {healthy ? "OK" : "Unavailable"}
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Offline mode" collapsible storageKey="offline-mode">
            <p className="mb-3 text-sm leading-relaxed text-muted-strong">
              With the connector offline, approvals are queued for publish rather
              than published, and flush automatically when the connection
              returns. Search and the assistant continue to work from the local
              index.
            </p>
            <button
              className={sync?.isOnline ? "btn-ghost" : "btn-accent"}
              disabled={offline.isPending}
              onClick={() => offline.mutate(!!sync?.isOnline)}
            >
              {sync?.isOnline ? "Simulate offline" : "Reconnect"}
            </button>
          </Card>
        </div>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3">
      <dt className="w-40 shrink-0 text-muted-soft">{label}</dt>
      <dd className="min-w-0 flex-1 break-words font-medium text-muted-strong">
        {value}
      </dd>
    </div>
  );
}
